import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { SidePanel, SidePanelContent, SidePanelFooter } from '@securspace/securspace-ui-kit';
import { Link } from 'react-router';
import { styled } from '@mui/material/styles';
import { requestPaymentMethods } from '../../../../paymentMethods/request/payment-method-requests';
import { getRentalAgreementURL } from '../../../../../util/BookingUtil';
import { requestCompleteBooking, updatePaymentMethod } from '../../../../bookedSpaces/request/booked-spaces-requests';
import { getErrorMessageForNonStandardAndStandardResponse } from '../../../../../util/NetworkErrorUtil';
import PaymentMethodSelect from '../modals/PaymentMethodSelect';
import AddPaymentMethodFlowManager from '../../../../paymentMethods/AddPaymentMethodFlowManager';
import { BankAccountStatus, PaymentType } from '../../../../constants/securspace-constants';
import RentalAgreementCheckbox from '../../../../RentalAgreementCheckbox';
import Busy from '../../../../Busy';
import { useSnackbar } from '../../../../../context/snackbar-context';
import type { Booking } from '../../../../../types/Booking';
import type { Account } from '../../../../../types/Account';
import type { PaymentMethod } from '../../../../../types/PaymentMethod';

const UpdatePaymentMethodPanelLink = styled(Link)(({ theme }) => ({
  fontSize: 'inherit',
  color: theme.palette.secondary.main,
  textDecoration: 'none',
}));

const StepLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1rem',
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1),
}));

const ProcessingFeeNotice = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  marginTop: theme.spacing(0.5),
}));

const UnverifiedSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.grey[50],
  borderLeft: `3px solid ${theme.palette.warning.main}`,
  borderRadius: '0.25rem',
}));

const UnverifiedItem = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  paddingTop: theme.spacing(0.5),
}));

interface UpdatePaymentMethodPanelProps {
  open: boolean;
  onClose: () => void;
  booking: Booking;
  account: Account;
  onSuccess: () => void;
}

const isUnverifiedMethod = (pm: PaymentMethod): boolean =>
  pm.dwollaStatus === BankAccountStatus.UNVERIFIED ||
  pm.stripeStatus === BankAccountStatus.UNVERIFIED;

const UpdatePaymentMethodPanel = (props: UpdatePaymentMethodPanelProps) => {
  const { open, onClose, booking, account, onSuccess } = props;
  const [verifiedMethods, setVerifiedMethods] = useState<PaymentMethod[]>([]);
  const [unverifiedMethods, setUnverifiedMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const [agreeToTermsChecked, agreeToTermsCheckedSet] = useState(false);
  const [addPaymentOpen, setAddPaymentOpen] = useState(false);
  const [reopenAfterAdd, setReopenAfterAdd] = useState(false);

  const { snackbarShowMessage } = useSnackbar();

  const loadMethods = useCallback(() => {
    if (!account) return;
    Busy.set(true);

    const handleSuccess = (methods: PaymentMethod[]) => {
      setVerifiedMethods(methods.filter((pm) => !isUnverifiedMethod(pm)));
      setUnverifiedMethods(methods.filter((pm) => isUnverifiedMethod(pm)));
      Busy.set(false);
    };

    const handleError = (error: unknown) => {
      Busy.set(false);
      snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(error), 'error');
    };

    requestPaymentMethods(account.id, handleSuccess, handleError, true);
  }, [account, snackbarShowMessage]);

  useEffect(() => {
    if (open) {
      loadMethods();
    }
  }, [open, loadMethods]);

  // When add payment method drawer closes and we flagged a reopen, reload methods and show the panel
  useEffect(() => {
    if (reopenAfterAdd && !addPaymentOpen) {
      setReopenAfterAdd(false);
      loadMethods();
    }
  }, [addPaymentOpen, reopenAfterAdd, loadMethods]);

  const resetForm = () => {
    setSelectedPaymentMethodId(null);
    agreeToTermsCheckedSet(false);
  };

  const onConfirm = async () => {
    if (!selectedPaymentMethodId) return;

    const bookingData = {
      bookingId: booking.id,
      paymentMethodId: selectedPaymentMethodId,
    };
    Busy.set(true);
    try {
      const response = await updatePaymentMethod(bookingData);
      const updatedBooking = response.body;
      await requestCompleteBooking({
        bookingId: updatedBooking.id,
        tosDocUrl: getRentalAgreementURL(booking.supplierLegalAgreementFileName),
      });
      resetForm();
      snackbarShowMessage('The payment method for this booking was updated successfully.', 'success');
      onSuccess();
    } catch (error) {
      snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(error), 'error');
    } finally {
      Busy.set(false);
    }
  };

  const handleFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = event.target;
    switch (name) {
      case 'paymentMethod':
        setSelectedPaymentMethodId(value);
        break;
      case 'agreeToTerms':
        agreeToTermsCheckedSet(checked);
        break;
      default:
        break;
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleAddPaymentMethod = () => {
    setReopenAfterAdd(true);
    setAddPaymentOpen(true);
  };

  const getPaymentMethodDescription = (pm: PaymentMethod): string => {
    const typeLabel = pm.type === PaymentType.CARD ? pm.cardBrand : pm.bankName;
    const nick = pm.nickName?.trim() ? `${pm.nickName}: ` : '';
    return `${nick}${typeLabel} ending in ${pm.lastFour}`;
  };

  const selectedPaymentMethod = verifiedMethods.find((pm) => pm.id === selectedPaymentMethodId);
  const displayProcessingFeesWarning = selectedPaymentMethod?.type === PaymentType.CARD;

  return (
    <>
      <SidePanel open={open && !addPaymentOpen} onClose={handleClose} title="Update Payment Method">
        <SidePanelContent>
          <Grid container flexDirection="column" gap={2}>
            <Grid container justifyContent="space-between" alignItems="center">
              <StepLabel variant="subtitle1" sx={{ mb: 0 }}>
                1. Select Payment Method
              </StepLabel>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddPaymentMethod}
                sx={{ textTransform: 'none' }}
              >
                Add New
              </Button>
            </Grid>

            <PaymentMethodSelect
              paymentMethods={verifiedMethods}
              onChange={handleFormChange}
              value={selectedPaymentMethodId}
            />
            {displayProcessingFeesWarning && (
              <ProcessingFeeNotice variant="body2">
                <InfoOutlinedIcon fontSize="small" />
                Additional credit card processing fee applies
              </ProcessingFeeNotice>
            )}

            {unverifiedMethods.length > 0 && (
              <UnverifiedSection>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <WarningAmberIcon fontSize="small" color="warning" />
                  Pending Verification
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  The following payment methods are not yet verified and cannot be selected until verification is complete.
                </Typography>
                {unverifiedMethods.map((pm) => (
                  <UnverifiedItem key={pm.id} variant="body2">
                    {getPaymentMethodDescription(pm)}
                  </UnverifiedItem>
                ))}
              </UnverifiedSection>
            )}

            <StepLabel variant="subtitle1" sx={{ mt: 1 }}>
              2. Accept{' '}
              <UpdatePaymentMethodPanelLink to="/terms-of-use" target="_blank" rel="noopener noreferrer">
                Terms of Use
              </UpdatePaymentMethodPanelLink>{' '}
              Agreement
            </StepLabel>
            <RentalAgreementCheckbox
              name="agreeToTerms"
              checked={agreeToTermsChecked}
              onChange={handleFormChange}
              required
            />
          </Grid>
        </SidePanelContent>
        <SidePanelFooter>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={4}>
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                onClick={handleClose}
                sx={{ py: 1.5, textTransform: 'none', fontSize: '1rem' }}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item xs={8}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={onConfirm}
                disabled={!selectedPaymentMethodId || !agreeToTermsChecked}
                sx={{ py: 1.5, textTransform: 'none', fontSize: '1rem' }}
              >
                Update
              </Button>
            </Grid>
          </Grid>
        </SidePanelFooter>
      </SidePanel>

      <AddPaymentMethodFlowManager
        account={account}
        open={addPaymentOpen}
        setOpen={setAddPaymentOpen}
        loadPaymentMethods={loadMethods}
      />
    </>
  );
};

export default UpdatePaymentMethodPanel;
